import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { UserDetails } from '../../api/UserAPI';
import { getBottomNavAPI, getCachedBottomNav, Product } from '../../api/ProductAPI';
import ROUTER_CONSTANT from '../../constants/RouterConstant';
import { Home } from '../common/Icon';
import { useSlidingIndicator } from '../../hooks/useSlidingIndicator';
import './footer.css';

type FooterProps = UserDetails & { onServicesClick?: () => void };

const ICON_SIZE = 26;
const STROKE = 1.9;
const STROKE_ACTIVE = 2.5;

const NavIcon: React.FC<{ lucideIcon?: string; active: boolean }> = ({ lucideIcon, active }) => {
  const Icon = lucideIcon ? (LucideIcons as any)[lucideIcon] : null;
  const strokeWidth = active ? STROKE_ACTIVE : STROKE;
  if (Icon) return <Icon size={ICON_SIZE} strokeWidth={strokeWidth} />;
  return <Home size={ICON_SIZE} strokeWidth={strokeWidth} />;
};

/**
 * Tab list is backend-driven (products table, product_type='BOTTOM_NAV', scoped per
 * role) — cached 30 min in localStorage by getBottomNavAPI so this always renders
 * instantly from cache first. Two routes get special click behavior (Home's
 * cache-bypass refresh, Services' modal override); matched by route constant rather
 * than label so relabeling a tile in the products table can't silently break them.
 */
const Footer: React.FC<FooterProps> = ({ onServicesClick, ...user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<Product[]>(() => getCachedBottomNav() ?? []);

  useEffect(() => {
    let cancelled = false;
    getBottomNavAPI(navigate).then(res => {
      if (!cancelled && res.length) setItems(res);
    });
    return () => { cancelled = true; };
  }, [navigate, user.userType]);

  const handleNavigation = useCallback((route: string, state?: any) => {
    navigate(route, { state });
  }, [navigate]);

  const handleClick = useCallback((route: string) => {
    if (route === ROUTER_CONSTANT.ROOT.HOME) {
      handleNavigation(route, { bypassMenuCache: true, ts: Date.now() });
      return;
    }
    if (route === ROUTER_CONSTANT.ROOT.ALL_MENU && onServicesClick) {
      onServicesClick();
      return;
    }
    handleNavigation(route);
  }, [handleNavigation, onServicesClick]);

  const visibleItems = items.filter(item => !item.locked);
  const isActive = (item: Product) => location.pathname === item.deeplink || location.pathname.startsWith(`${item.deeplink}/`);
  const activeIndex = visibleItems.findIndex(isActive);

  // The gold pill glides to the active tab instead of the tab's own background switching.
  const navRef = useRef<HTMLElement>(null);
  const pill = useSlidingIndicator(navRef, activeIndex, [visibleItems.length]);
  // Armed one frame after the first measurement, so the pill doesn't fly in from x=0 on mount.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!pill.ready) return;
    const id = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(id);
  }, [pill.ready]);

  if (!visibleItems.length) return null;

  return (
    <nav className="bottom-navigation" aria-label="Primary navigation" ref={navRef}>
      {activeIndex >= 0 && (
        <span
          className="bottom-navigation__pill"
          aria-hidden="true"
          style={{ transform: pill.transform, width: pill.width, transition: armed ? undefined : 'none', opacity: pill.ready ? 1 : 0 }}
        />
      )}
      {visibleItems.map((item, i) => {
        const active = i === activeIndex;
        return (
          <button
            key={item.name}
            type="button"
            data-pill-index={i}
            className={`bottom-navigation__item${active ? ' bottom-navigation__item--active' : ''}`}
            aria-label={item.name}
            aria-current={active ? 'page' : undefined}
            onClick={() => handleClick(item.deeplink)}
          >
            <span className="bottom-navigation__icon" aria-hidden="true"><NavIcon lucideIcon={item.lucideIcon} active={active} /></span>
          </button>
        );
      })}
    </nav>
  );
};

export default Footer;
